<template>
  <div class="grid-container">
    <ag-grid-vue
      class="ag-theme-balham"
      style="width: 100%; height: 100%"
      :columnDefs="columnDefs"
      :rowData="displayItems"
      :defaultColDef="defaultColDef"
      :suppressMovableColumns="true"
      :suppressRowClickSelection="true"
      :rowSelection="null"
      :animateRows="true"
      :context="context"
      :overlayLoadingTemplate="loadingTemplate"
      :overlayNoRowsTemplate="noRowsTemplate"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      @cell-value-changed="onCellValueChanged"
      @cell-clicked="onCellClicked"
      @first-data-rendered="onFirstDataRendered"
      @model-updated="onModelUpdated"
      @row-data-updated="onRowDataUpdated"
      @viewport-changed="onViewportChanged"
      @body-scroll="onBodyScroll"
      @displayed-columns-changed="onDisplayedColumnsChanged"
      @animation-queue-empty="onAnimationQueueEmpty"
    />
    
    <v-dialog v-model="showColorPicker" max-width="400">
      <v-card class="color-picker-dialog">
        <v-card-title class="text-h6">Select Color</v-card-title>
        <v-card-text>
          <v-color-picker
            v-model="selectedColor"
            mode="hex"
            width="350"
            flat
          ></v-color-picker>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" color="primary" @click="applyColor">APPLY</v-btn>
          <v-btn variant="text" @click="showColorPicker = false">CANCEL</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { defineComponent } from 'vue'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import apiService from '@/services/api'
import { mapState, mapActions } from 'vuex'

export default defineComponent({
  name: 'RiskLevelsGrid',
  components: {
    AgGridVue
  },
  emits: ['grid-ready', 'cell-value-changed', 'update-success', 'update-error', 'delete-level'],
  props: {
    filtered: {
      type: Boolean,
      default: false
    },
    filteredItems: {
      type: Array,
      default: () => []
    },
    additionalColumns: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapState({
      riskLevels: state => state.levels.riskLevels,
      isLoading: state => state.levels.loading.riskLevels,
      error: state => state.levels.error.riskLevels
    }),
    displayItems() {
      return this.filtered ? this.filteredItems : this.riskLevels
    }
  },
  data() {
    return {
      gridApi: null,
      columnApi: null,
      isReverting: false,
      showColorPicker: false,
      selectedColor: '#000000',
      activeCell: null,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 100
      },
      columnDefs: [
        {
          field: 'id',
          headerName: 'ID',
          width: 100,
          editable: false,
          valueFormatter: params => params.value || 0
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
          editable: true,
          cellEditor: 'agTextCellEditor'
        },
        {
          field: 'description',
          headerName: 'Description',
          flex: 2,
          editable: true,
          cellEditor: 'agTextCellEditor'
        },
        {
          field: 'color',
          headerName: 'Color',
          editable: true,
          cellRenderer: params => {
            const color = params.value || '#CCCCCC';
            return `
              <div style="display: flex; align-items: center; height: 100%;">
                <div style="
                  width: 24px;
                  height: 24px;
                  border-radius: 4px;
                  background-color: ${color};
                  border: 1px solid #ddd;
                  margin-right: 8px;
                "></div>
                <span>${color}</span>
              </div>
            `;
          },
          cellEditor: 'colorPickerEditor',
          cellEditorPopup: true,
          width: 120
        },
        {
          headerName: '',
          field: 'actions',
          width: 50,
          maxWidth: 50,
          editable: false,
          sortable: false,
          filter: false,
          resizable: false,
          cellRenderer: () => {
            return '<span class="delete-btn" title="Delete level" style="cursor:pointer; color:#999; display:flex; align-items:center; justify-content:center; height:100%; font-size:16px;">&times;</span>';
          }
        }
      ],
      context: {
        componentParent: this
      },
      loadingTemplate: 
        '<span class="ag-overlay-loading-center">Loading data...</span>',
      noRowsTemplate:
        '<span class="ag-overlay-no-rows-center">No data to display</span>',
      apiService: apiService,
      gridOptions: {
        rowDragManaged: true,
        animateRows: true,
        suppressMoveWhenRowDragging: false,
        getRowId: params => params.data.id,
        domLayout: 'normal'
      },
      components: null
    }
  },
  watch: {
    additionalColumns: {
      handler(newColumns) {
        console.log('RiskLevelsGrid: Additional columns updated:', newColumns);
        this.updateColumnDefs(newColumns);
      },
      immediate: true
    }
  },
  created() {
    console.log('RiskLevelsGrid: Component created');
    if (!this.filtered) {
      this.fetchRiskLevels({ apiService });
    }
  },
  mounted() {
    console.log('RiskLevelsGrid: Component mounted');
  },
  methods: {
    ...mapActions('levels', ['fetchRiskLevels', 'updateRiskLevel']),
    
    onGridReady(params) {
      console.log('RiskLevelsGrid: Grid ready event received');
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
      
      // Set initial data
      this.$nextTick(() => {
        console.log('RiskLevelsGrid: Setting initial row data:', this.displayItems);
        this.gridApi.setRowData(this.displayItems);
        this.gridApi.sizeColumnsToFit();
        this.gridApi.redrawRows();
        
        // Force a refresh after a short delay
        setTimeout(() => {
          this.gridApi.redrawRows();
          this.gridApi.sizeColumnsToFit();
        }, 100);
      });

      // Emit grid-ready event
      this.$emit('grid-ready', params);
    },
    onFirstDataRendered() {
      console.log('RiskLevelsGrid: first-data-rendered', { time: Date.now(), rows: this.displayItems?.length || 0 });
    },
    onModelUpdated() {
      console.log('RiskLevelsGrid: model-updated', { time: Date.now() });
    },
    onRowDataUpdated() {
      console.log('RiskLevelsGrid: row-data-updated', { time: Date.now(), rows: this.displayItems?.length || 0 });
    },
    onViewportChanged() {
      const rc = this.gridApi?.getDisplayedRowCount?.() ?? 0;
      console.log('RiskLevelsGrid: viewport-changed', { time: Date.now(), displayedRowCount: rc });
    },
    onBodyScroll() {
      console.log('RiskLevelsGrid: body-scroll', { time: Date.now() });
    },
    onDisplayedColumnsChanged() {
      console.log('RiskLevelsGrid: displayed-columns-changed', { time: Date.now() });
    },
    onAnimationQueueEmpty() {
      console.log('RiskLevelsGrid: animation-queue-empty', { time: Date.now() });
    },
    
    async onCellValueChanged(event) {
      if (this.isReverting) {
        console.log('RiskLevelsGrid: Skipping update while reverting');
        return;
      }

      console.log('RiskLevelsGrid: Cell value changed:', event);

      try {
        const update = {
          id: event.data.id,
          [event.column.colId]: event.newValue
        };
        
        const response = await this.updateRiskLevel({ 
          apiService,
          update
        });
        
        // Force refresh the specific cell with the response data
        if (event.column.colId === 'color') {
          const node = this.gridApi.getRowNode(event.data.id.toString());
          if (node) {
            node.setDataValue('color', response.color);
            this.gridApi.refreshCells({
              rowNodes: [node],
              columns: ['color'],
              force: true
            });
          }
        }
        
        this.$emit('update-success', {
          item: event.data,
          field: event.column.colId,
          newValue: event.newValue
        });
      } catch (error) {
        console.error('Failed to update risk level:', error);
        this.revertCellValue(event);
        
        this.$emit('update-error', {
          item: event.data,
          field: event.column.colId,
          error: error.message || 'Failed to update'
        });
      }
    },

    revertCellValue(event) {
      this.isReverting = true;
      
      // Stop editing before attempting to revert
      this.gridApi.stopEditing();
      
      // Use setTimeout to break the event chain
      setTimeout(() => {
        const node = this.gridApi.getRowNode(event.data.id?.toString() || '0');
        if (node) {
          node.setDataValue(event.column.colId, event.oldValue);
        }
        
        setTimeout(() => {
          this.isReverting = false;
        }, 50);
      }, 0);
    },
    
    updateColumnDefs(additionalColumns) {
      if (additionalColumns && additionalColumns.length > 0) {
        // Process additional columns and handle insertAfter positioning
        const columns = [...this.columnDefs];
        
        additionalColumns.forEach(additionalCol => {
          // Create a clean column object without custom properties
          const { insertAfter, ...cleanCol } = additionalCol;
          
          if (insertAfter) {
            // Find the index of the column to insert after
            const insertAfterIndex = columns.findIndex(col => col.field === insertAfter);
            if (insertAfterIndex !== -1) {
              // Insert after the found column
              columns.splice(insertAfterIndex + 1, 0, cleanCol);
            } else {
              // If column not found, append to the end
              columns.push(cleanCol);
            }
          } else {
            // No specific position, append to the end
            columns.push(cleanCol);
          }
        });
        
        this.columnDefs = columns;
      }
    },

    onCellClicked(event) {
      if (event.column.colId === 'actions') {
        this.$emit('delete-level', event.data);
        return;
      }
      if (event.column.colId === 'color') {
        this.activeCell = event;
        this.selectedColor = event.value;
        this.showColorPicker = true;
      }
    },

    async applyColor() {
      if (!this.activeCell) return;

      const event = {
        data: this.activeCell.data,
        oldValue: this.activeCell.value,
        newValue: this.selectedColor,
        column: this.activeCell.column
      };

      try {
        await this.onCellValueChanged(event);
      } finally {
        this.showColorPicker = false;
        this.activeCell = null;
      }
    }
  }
})
</script>

<style scoped>
.grid-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

/* Align cell wrapper center */
:deep(.ag-cell-wrapper) {
  align-items: center;
}

:deep(.color-picker-dialog) {
  .v-card-title {
    padding: 16px 16px 0;
  }
  
  .v-card-text {
    padding: 16px;
    padding-bottom: 0;
  }

  .v-card-actions {
    padding: 16px;
  }
}

:deep(.ag-row) {
  border-bottom: var(--ag-row-border-width)
                 var(--ag-row-border-style)
                 var(--ag-row-border-color);
}
</style> 